import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, MenuItem, Stack, TextField,
  Typography,
} from "@mui/material";
import { ExternalLink, FolderPlus, Pencil, Search, Trash2 } from "lucide-react";
import {
  createResource, createResourceCategory, deleteResource, getResourceCategories,
  updateResource,
} from "./resourcesApi";
import { ResourceCategory, ResourceItem } from "./types";
import ResourcePreview from "./ResourcePreview";
import { getResourcePresentation } from "./resourcePresentation";
import { toast } from "react-toastify";

const emptyForm = { title: "", description: "", url: "", category: "", tags: "" };
const slugify = (value: string) => value.toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const detectType = (url: string): ResourceItem["resource_type"] => {
  const value = url.toLowerCase();
  if (value.includes("docs.google.com/spreadsheets") || value.includes("sheets.google.com")) return "google_sheets";
  if (value.includes("docs.google.com/document")) return "google_docs";
  if (value.includes("drive.google.com")) return "google_drive";
  if (value.endsWith(".pdf") || value.includes(".pdf?")) return "pdf";
  return "website";
};

interface Props {
  resources: ResourceItem[];
  onChange: React.Dispatch<React.SetStateAction<ResourceItem[]>>;
}

export default function ResourcesDrawerPanel({ resources, onChange }: Props) {
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ResourceItem | null>(null);
  const [deleting, setDeleting] = useState<ResourceItem | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categorySaving, setCategorySaving] = useState(false);

  useEffect(() => {
    getResourceCategories()
      .then(setCategories)
      .catch(() => toast.error("Categories could not be loaded."));
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return resources.filter((resource) =>
      (category === "all" || resource.category_slug === category) &&
      (!needle || `${resource.title} ${resource.description} ${resource.tags.join(" ")}`
        .toLowerCase().includes(needle)));
  }, [resources, search, category]);

  const openResource = async (resource: ResourceItem) => {
    const cleanUrl = resource.destination_url.split("?")[0];
    const extension = cleanUrl.split(".").pop()?.toLowerCase() || "";
    const downloadable = ["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"];
    if (!downloadable.includes(extension)) {
      window.open(resource.destination_url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const response = await fetch(resource.destination_url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = cleanUrl.split("/").pop() || resource.title;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("The document could not be downloaded.");
    }
  };

  const openEdit = (resource: ResourceItem) => {
    setEditing(resource);
    setForm({
      title: resource.title,
      description: resource.description,
      url: resource.destination_url,
      category: String(resource.category),
      tags: resource.tags.join(", "),
    });
    setFormOpen(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: editing?.slug || `${slugify(form.title)}-${Date.now().toString().slice(-6)}`,
        description: form.description.trim(),
        tags: form.tags.split(",").map((tag) => tag.trim().toLowerCase()).filter(Boolean),
        category: Number(form.category),
        resource_type: detectType(form.url),
        url: form.url.trim(),
        is_featured: false,
        is_published: true,
      };
      const saved = editing
        ? await updateResource(editing.id, payload)
        : await createResource(payload);
      onChange((current) => editing
        ? current.map((item) => item.id === saved.id ? saved : item)
        : [saved, ...current]);
      setFormOpen(false);
      setEditing(null);
      toast.success(editing
        ? "Document or link updated successfully."
        : "Document or link added successfully.");
    } catch {
      toast.error("The document or link could not be saved.");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await deleteResource(deleting.id);
      onChange((current) => current.filter((item) => item.id !== deleting.id));
      setDeleting(null);
      toast.success("Document or link deleted successfully.");
    } catch {
      toast.error("The document or link could not be deleted.");
    } finally { setSaving(false); }
  };

  const submitCategory = async (event: FormEvent) => {
    event.preventDefault();
    const name = newCategoryName.trim();
    if (!name) return;
    setCategorySaving(true);
    try {
      const created = await createResourceCategory({
        name,
        slug: slugify(name),
        color: "#805746",
      });
      setCategories((current) => [...current, created]
        .sort((left, right) => left.name.localeCompare(right.name)));
      setCategory(created.slug);
      setNewCategoryName("");
      setCategoryDialogOpen(false);
      toast.success("Category added successfully.");
    } catch {
      toast.error("The category could not be added. A category with that name may already exist.");
    } finally {
      setCategorySaving(false);
    }
  };

  return <Box>
    <Stack direction="row" gap={1} mb={2} sx={{ position: "sticky", top: 0, zIndex: 2, py: 0.5, bgcolor: "background.default" }}>
      <TextField size="small" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search docs & links" InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment> }} sx={{ flex: 1, minWidth: 0 }} />
      <TextField select size="small" value={category} onChange={(event) => setCategory(event.target.value)} sx={{ width: 132, flexShrink: 0 }}>
        <MenuItem value="all">All categories</MenuItem>
        {categories.map((item) => <MenuItem key={item.id} value={item.slug}>{item.name}</MenuItem>)}
      </TextField>
      <IconButton
        aria-label="Add Docs and Links category"
        title="Add category"
        onClick={() => setCategoryDialogOpen(true)}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, flexShrink: 0 }}
      >
        <FolderPlus size={17} />
      </IconButton>
    </Stack>

    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2 }}>
      {filtered.map((resource) => {
        const presentation = getResourcePresentation(resource);
        return <Box
        key={resource.id}
        sx={{
          minWidth: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
          position: "relative",
          "&:hover .resource-actions": { opacity: 1 },
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => void openResource(resource)}
          sx={{
            width: "100%",
            border: 0,
            height: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: resource.category_color || "primary.main",
            background: `linear-gradient(145deg, ${resource.category_color || "#805746"}20, ${resource.category_color || "#805746"}08)`,
            textDecoration: "none",
            cursor: "pointer",
          }}
        >
          <ResourcePreview resource={resource} />
        </Box>

        <Stack
          className="resource-actions"
          direction="row"
          spacing={0.5}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            opacity: { xs: 1, md: 0 },
            transition: "opacity .2s",
          }}
        >
          <IconButton size="small" onClick={() => openEdit(resource)} aria-label={`Edit ${resource.title}`} sx={{ color: "white", bgcolor: "rgba(0,0,0,.48)", "&:hover": { bgcolor: "rgba(0,0,0,.7)" } }}><Pencil size={14} /></IconButton>
          <IconButton size="small" onClick={() => setDeleting(resource)} aria-label={`Delete ${resource.title}`} sx={{ color: "white", bgcolor: "rgba(0,0,0,.48)", "&:hover": { bgcolor: "error.main" } }}><Trash2 size={14} /></IconButton>
        </Stack>

        <Box sx={{ p: 0.8 }}>
          <Typography
            fontWeight={750}
            fontSize={11}
            lineHeight={1.25}
            sx={{ minHeight: "2.5em", overflowWrap: "anywhere" }}
            title={resource.title}
          >
            {resource.title}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7, display: "block", fontSize: 9.5 }} noWrap>{resource.category_name}</Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.75}>
            <Chip label={presentation.label} size="small" sx={{ maxWidth: 72, height: 19, fontSize: 8.5 }} />
            <IconButton onClick={() => void openResource(resource)} size="small" aria-label={`Open ${resource.title}`}><ExternalLink size={14} /></IconButton>
          </Stack>
        </Box>
      </Box>;})}
      {filtered.length === 0 && <Box textAlign="center" py={5} sx={{ gridColumn: "1 / -1" }}><Typography fontWeight={700}>No docs or links found</Typography><Typography variant="body2" sx={{ opacity: 0.7 }}>Add a document or link, or adjust the filters.</Typography></Box>}
    </Box>

    <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <Box component="form" onSubmit={submit}>
        <DialogTitle fontWeight={800}>{editing ? "Edit Document or Link" : "Add Document or Link"}</DialogTitle>
        <DialogContent><Stack spacing={2} pt={1}>
          {categories.length === 0 && <Alert severity="info">No Docs &amp; Links categories are available yet.</Alert>}
          <TextField required label="Name" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          <TextField label="Description" multiline minRows={2} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
          <TextField required type="url" label="Link URL" value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} />
          <TextField required select label="Category" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>{categories.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</TextField>
          <TextField label="Tags" helperText="Separate tags with commas" value={form.tags} onChange={(event) => setForm({ ...form, tags: event.target.value })} />
        </Stack></DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button><Button type="submit" variant="contained" disabled={saving || categories.length === 0}>{saving ? "Saving..." : editing ? "Save Changes" : "Add Document or Link"}</Button></DialogActions>
      </Box>
    </Dialog>

    <Dialog
      open={categoryDialogOpen}
      onClose={() => !categorySaving && setCategoryDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box component="form" onSubmit={submitCategory}>
        <DialogTitle fontWeight={800}>Add category</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            Create a category for organising documents and links.
          </Typography>
          <TextField
            autoFocus
            required
            fullWidth
            label="Category name"
            placeholder="e.g. Policies"
            value={newCategoryName}
            onChange={(event) => setNewCategoryName(event.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setCategoryDialogOpen(false)} disabled={categorySaving}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={categorySaving || !newCategoryName.trim()}>
            {categorySaving ? "Adding..." : "Add Category"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>

    <Dialog open={Boolean(deleting)} onClose={() => !saving && setDeleting(null)} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={800}>Delete this document or link?</DialogTitle>
      <DialogContent><Typography>“{deleting?.title}” will be removed from MMS. The original linked document will not be deleted.</Typography></DialogContent>
      <DialogActions><Button onClick={() => setDeleting(null)} disabled={saving}>Cancel</Button><Button color="error" variant="contained" onClick={() => void confirmDelete()} disabled={saving}>Delete</Button></DialogActions>
    </Dialog>
  </Box>;
}
